import React from 'react';

export function SaltTypes() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Understanding Salt in Bread Baking</h3>
        <p className="mb-4">
          Salt plays a crucial role in bread baking beyond just flavor. It affects fermentation rate, dough strength, and final texture. Understanding different salt types and their proper use is essential for successful bread baking.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Salt Percentage in Bread</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Recommended Range: 1.8-2.2%</h4>
            <p className="text-sm text-white/80">
              Salt content is expressed as a percentage of flour weight. For most breads, 2% is standard.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Too Little Salt (less than 1.5%)</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Bland flavor</li>
                  <li>Over-active fermentation</li>
                  <li>Weak gluten structure</li>
                  <li>Poor crust color</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Too Much Salt (over 2.5%)</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Overly salty taste</li>
                  <li>Slow fermentation</li>
                  <li>Tough dough</li>
                  <li>Dense crumb</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Types of Salt for Bread Baking</h3>
        
        <div className="space-y-6">
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Fine Sea Salt</h4>
            <p className="text-sm text-white/80 mt-1">
              Pure salt with fine, uniform crystals.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Dissolves easily</li>
                  <li>Consistent measurements</li>
                  <li>Clean taste</li>
                  <li>No additives</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Most bread recipes</li>
                  <li>Sourdough starters</li>
                  <li>Precise measurements</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Kosher Salt</h4>
            <p className="text-sm text-white/80 mt-1">
              Larger, flaky crystals with no additives.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Pure flavor</li>
                  <li>Easy to pinch and sprinkle</li>
                  <li>No iodine or anti-caking agents</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Considerations:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Different brands have varying densities</li>
                  <li>May need weight adjustments</li>
                  <li>Takes longer to dissolve</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Table Salt</h4>
            <p className="text-sm text-white/80 mt-1">
              Fine-grained salt with additives like iodine.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Readily available</li>
                  <li>Dissolves quickly</li>
                  <li>Consistent grain size</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Disadvantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Contains additives</li>
                  <li>Can impart metallic taste</li>
                  <li>May affect starter health</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Specialty Salts</h4>
            <p className="text-sm text-white/80 mt-1">
              Includes fleur de sel, Himalayan pink salt, Celtic sea salt, etc.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Advantages:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Unique mineral profiles</li>
                  <li>Complex flavors</li>
                  <li>Additional trace minerals</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Best Uses:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Finishing salt on crust</li>
                  <li>Special artisan breads</li>
                  <li>When mineral content is desired</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Salt's Effects on Bread</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Fermentation Control</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Slows yeast activity</li>
                <li>Controls fermentation rate</li>
                <li>Affects enzyme activity</li>
                <li>Influences bacterial growth</li>
              </ul>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Dough Structure</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Strengthens gluten bonds</li>
                <li>Improves dough elasticity</li>
                <li>Enhances gas retention</li>
                <li>Affects crumb texture</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Best Practices for Salt Use</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Measuring Salt</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Always measure by weight, not volume</li>
              <li>Account for different salt densities</li>
              <li>Keep measurements consistent</li>
              <li>Consider salt's moisture content</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Adding Salt to Dough</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Add after autolyse period</li>
              <li>Dissolve in small amount of water if needed</li>
              <li>Mix thoroughly into dough</li>
              <li>Keep salt separate from yeast initially</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Storage Tips</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Keep in airtight container</li>
              <li>Store in dry place</li>
              <li>Avoid metal containers</li>
              <li>Label different salt types clearly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}